import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Heading,
  VStack,
  HStack,
  Flex,
  Text,
  Tag as ChakraTag,
  TagLabel,
  Input,
  Button,
  IconButton,
  useColorModeValue,
  Spinner,
  InputGroup,
  InputRightElement,
  useToast,
  Divider,
  useBreakpointValue,
  Grid,
  GridItem,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useAtom, useSetAtom } from 'jotai';
import { tagsAtom, fetchTagsAtom, createTagAtom, updateTagAtom, deleteTagAtom, tagsLoadingAtom } from '../../atoms/v0/tagAtoms';
import { MdAdd, MdDelete, MdEdit, MdCheck, MdClose, MdWarning } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../components/v0/PageLayout';
import { Tag } from '../../types/tag';

// 标签排序方式
type SortOption = 'alpha-asc' | 'alpha-desc' | 'date-new' | 'date-old';

// 合并确认对话框数据
interface MergeConfirmData {
  sourceTagId: string;
  sourceTagTitle: string;
  targetTagId: string;
  targetTagTitle: string;
  taskCount: number;
}

const TagsPage: React.FC = () => {
  const { t } = useTranslation(['v0/common', 'v0/tasks', 'common']);
  const navigate = useNavigate();
  const toast = useToast();

  // 标签状态
  const [tags, setTags] = useAtom(tagsAtom);
  const [isLoading] = useAtom(tagsLoadingAtom);
  const fetchTags = useSetAtom(fetchTagsAtom);
  const createTag = useSetAtom(createTagAtom);
  const updateTag = useSetAtom(updateTagAtom);
  const deleteTag = useSetAtom(deleteTagAtom);

  // UI 状态
  const [newTagTitle, setNewTagTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('alpha-asc');

  // 合并标签对话框
  const { isOpen: isMergeAlertOpen, onOpen: onMergeAlertOpen, onClose: onMergeAlertClose } = useDisclosure();
  const [mergeConfirmData, setMergeConfirmData] = useState<MergeConfirmData | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [isMerging, setIsMerging] = useState(false);

  // 响应式设计
  const gridTemplateColumns = useBreakpointValue({
    base: 'repeat(1, 1fr)',
    sm: 'repeat(2, 1fr)',
    md: 'repeat(3, 1fr)',
    lg: 'repeat(4, 1fr)'
  });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'sm' });
  const iconSpacing = useBreakpointValue({ base: 1, md: 1 });

  // 颜色
  const tagBg = useColorModeValue('gray.100', 'gray.700');
  const tagBorder = useColorModeValue('gray.200', 'gray.600');
  const editingTagBg = useColorModeValue('white', 'gray.800');

  // 加载标签
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // 创建新标签
  const handleCreateTag = async () => {
    if (!newTagTitle.trim()) return;

    try {
      setIsCreating(true);
      const success = await createTag({ title: newTagTitle.trim() });

      if (success) {
        setNewTagTitle('');
        toast({
          title: t('v0/tasks:tags.createSuccess'),
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      } else {
        toast({
          title: t('v0/tasks:tags.createError'),
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      }
    } finally {
      setIsCreating(false);
    }
  };

  // 开始编辑标签
  const handleStartEdit = (tagId: string, currentTitle: string) => {
    setEditingTagId(tagId);
    setEditingTitle(currentTitle);
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditingTagId(null);
    setEditingTitle('');
  };

  // 检查是否需要合并标签
  const checkTagMerge = (newTitle: string, currentTagId: string): Tag | null => {
    // 忽略大小写比较
    const normalizedTitle = newTitle.toLowerCase().trim();

    // 查找是否存在同名但ID不同的标签
    const existingTag = tags.find(tag =>
      tag.id !== currentTagId &&
      tag.title.toLowerCase().trim() === normalizedTitle
    );

    return existingTag || null;
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    if (!editingTagId || !editingTitle.trim()) {
      handleCancelEdit();
      return;
    }

    // 检查是否存在同名标签
    const existingTag = checkTagMerge(editingTitle.trim(), editingTagId);

    if (existingTag) {
      // 存在同名标签，提示用户是否合并
      const currentTag = tags.find(tag => tag.id === editingTagId);
      if (!currentTag) {
        handleCancelEdit();
        return;
      }

      // 打开合并确认对话框
      setMergeConfirmData({
        sourceTagId: editingTagId,
        sourceTagTitle: currentTag.title,
        targetTagId: existingTag.id,
        targetTagTitle: existingTag.title,
        taskCount: 0, // 实际场景中应从API获取关联任务数量
      });
      onMergeAlertOpen();
      return;
    }

    // 没有同名标签，正常更新
    try {
      const success = await updateTag({ id: editingTagId, title: editingTitle.trim() });

      if (success) {
        toast({
          title: t('v0/tasks:tags.updateSuccess'),
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      } else {
        toast({
          title: t('v0/tasks:tags.updateError'),
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      }
    } finally {
      handleCancelEdit();
    }
  };

  // 合并标签
  const handleMergeTags = async () => {
    if (!mergeConfirmData) return;

    try {
      setIsMerging(true);

      // 这里调用API合并标签
      // 伪代码:
      // const success = await mergeTagsService.mergeTags(mergeConfirmData.sourceTagId, mergeConfirmData.targetTagId);

      // 临时方案: 直接删除源标签（实际应该是服务端合并，迁移所有关联）
      const success = await deleteTag(mergeConfirmData.sourceTagId);

      if (success) {
        toast({
          title: t('v0/tasks:tags.mergeSuccess'),
          description: t('v0/tasks:tags.mergeSuccessDescription', {
            source: mergeConfirmData.sourceTagTitle,
            target: mergeConfirmData.targetTagTitle
          }),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });

        // 刷新标签列表
        fetchTags();
      } else {
        toast({
          title: t('v0/tasks:tags.mergeError'),
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error) {
      console.error(t('v0/tasks:tags.mergeErrorLog'), error);
      toast({
        title: t('v0/tasks:tags.mergeError'),
        description: t('v0/tasks:tags.mergeErrorDescription'),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsMerging(false);
      onMergeAlertClose();
      handleCancelEdit();
    }
  };

  // 删除标签
  const handleDeleteTag = async (tagId: string) => {
    try {
      const success = await deleteTag(tagId);

      if (success) {
        toast({
          title: t('v0/tasks:tags.deleteSuccess'),
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      } else {
        toast({
          title: t('v0/tasks:tags.deleteError'),
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error) {
      console.error(t('v0/tasks:tags.deleteErrorLog'), error);
    }
  };

  // 过滤和排序标签
  const sortedAndFilteredTags = React.useMemo(() => {
    // 过滤
    let result = tags.filter(tag =>
      searchQuery === '' || tag.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // 排序
    switch (sortOption) {
      case 'alpha-asc':
        result = [...result].sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'alpha-desc':
        result = [...result].sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'date-new':
        result = [...result].sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime());
        break;
      case 'date-old':
        result = [...result].sort((a, b) => new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime());
        break;
    }

    return result;
  }, [tags, searchQuery, sortOption]);

  return (
    <PageLayout title={t('v0/tasks:tags.manageTagsTitle', '管理标签')} showBackButton={true}>
      <Container maxW="container.md" py={4}>
        <VStack spacing={4} align="stretch">

          {/* 搜索和创建 */}
          <Box>
            <Flex mb={3} gap={2} direction={{ base: 'column', md: 'row' }}>
              <Input
                placeholder={t('v0/tasks:tags.searchTags', '搜索标签...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size={buttonSize}
                flex={{ md: 1 }}
              />

              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value as SortOption)}
                size={buttonSize}
                width={{ base: 'full', md: '180px' }}
              >
                <option value="alpha-asc">{t('v0/tasks:tags.sortAlphaAsc')}</option>
                <option value="alpha-desc">{t('v0/tasks:tags.sortAlphaDesc')}</option>
                <option value="date-new">{t('v0/tasks:tags.sortDateNew')}</option>
                <option value="date-old">{t('v0/tasks:tags.sortDateOld')}</option>
              </Select>
            </Flex>

            <InputGroup size={buttonSize}>
              <Input
                placeholder={t('v0/tasks:tags.newTagPlaceholder', '输入新标签名称...')}
                value={newTagTitle}
                onChange={(e) => setNewTagTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateTag();
                  }
                }}
                isDisabled={isCreating}
              />
              <InputRightElement width="4.5rem">
                <Button
                  h="1.75rem"
                  size="sm"
                  colorScheme="teal"
                  onClick={handleCreateTag}
                  isLoading={isCreating}
                  isDisabled={!newTagTitle.trim()}
                >
                  {t('v0/common:create', '创建')}
                </Button>
              </InputRightElement>
            </InputGroup>
          </Box>

          <Divider />

          {/* 标签列表 */}
          <Box mt={2}>
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="md" fontWeight="bold">
                {t('v0/tasks:tags.existingTags', '现有标签')}
                {' '}{isLoading && <Spinner size="sm" ml={2} color="teal.500" />}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {t('v0/tasks:tags.tagCount', { count: sortedAndFilteredTags.length })}
              </Text>
            </Flex>

            {isLoading && tags.length === 0 ? (
              <Flex justify="center" py={6}>
                <Spinner size="lg" color="teal.500" />
              </Flex>
            ) : sortedAndFilteredTags.length > 0 ? (
              <Grid templateColumns={gridTemplateColumns} gap={2}>
                {sortedAndFilteredTags.map((tag) => (
                  <GridItem key={tag.id}>
                    {editingTagId === tag.id ? (
                      <Flex p={2} borderWidth="1px" borderRadius="md" bg={editingTagBg} alignItems="center">
                        <Input
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          autoFocus
                          size="sm"
                          mr={1}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveEdit();
                            } else if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                        />
                        <IconButton
                          aria-label="Save edit"
                          icon={<MdCheck />}
                          size="xs"
                          colorScheme="green"
                          onClick={handleSaveEdit}
                          mr={1}
                        />
                        <IconButton
                          aria-label="Cancel edit"
                          icon={<MdClose />}
                          size="xs"
                          onClick={handleCancelEdit}
                        />
                      </Flex>
                    ) : (
                      <Flex
                        p={2}
                        borderWidth="1px"
                        borderColor={tagBorder}
                        borderRadius="md"
                        bg={tagBg}
                        justifyContent="space-between"
                        alignItems="center"
                        _hover={{ bg: useColorModeValue('gray.200', 'gray.600') }}
                      >
                        <Text
                          fontWeight="medium"
                          fontSize="sm"
                          isTruncated
                          maxW="65%"
                          title={tag.title}
                        >
                          {tag.title}
                        </Text>
                        <HStack spacing={1}>
                          <IconButton
                            aria-label="Edit tag"
                            icon={<MdEdit />}
                            size="xs"
                            variant="ghost"
                            onClick={() => handleStartEdit(tag.id, tag.title)}
                          />
                          <IconButton
                            aria-label="Delete tag"
                            icon={<MdDelete />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteTag(tag.id)}
                          />
                        </HStack>
                      </Flex>
                    )}
                  </GridItem>
                ))}
              </Grid>
            ) : (
              <Box textAlign="center" py={6}>
                <Text fontSize="md" color="gray.500">
                  {searchQuery
                    ? t('v0/tasks:tags.noTagsMatchSearch', '没有匹配的标签')
                    : t('v0/tasks:tags.noTagsYet', '尚未创建标签')}
                </Text>
              </Box>
            )}
          </Box>
        </VStack>
      </Container>

      {/* 标签合并确认对话框 */}
      <AlertDialog
        isOpen={isMergeAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onMergeAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('v0/tasks:tags.mergeConfirmTitle')}
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text>{t('v0/tasks:tags.mergeConfirmQuestion')}</Text>
                </Box>

                <Box p={3} borderWidth="1px" borderRadius="md" bg={useColorModeValue('orange.50', 'gray.700')}>
                  <HStack spacing={3}>
                    <WarningIcon color="orange.500" />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">{t('v0/tasks:tags.willMerge')}</Text>
                      <Text><Text as="span" fontWeight="bold">{t('v0/tasks:tags.from')}</Text> {mergeConfirmData?.sourceTagTitle}</Text>
                      <Text><Text as="span" fontWeight="bold">{t('v0/tasks:tags.to')}</Text> {mergeConfirmData?.targetTagTitle}</Text>
                    </VStack>
                  </HStack>
                </Box>

                <Text fontSize="sm" color="gray.500">
                  {t('v0/tasks:tags.mergeDescription', {
                    source: mergeConfirmData?.sourceTagTitle,
                    target: mergeConfirmData?.targetTagTitle
                  })}
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onMergeAlertClose}>
                {t('v0/common:cancel')}
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleMergeTags}
                ml={3}
                isLoading={isMerging}
                loadingText={t('v0/tasks:tags.merging')}
              >
                {t('v0/tasks:tags.mergeTags')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageLayout>
  );
};

export { TagsPage };